import React, { useEffect, useMemo, useState } from "react";
import {
  ICustomerType,
  WALLET_WHITELIST_TAB_TYPE,
  WhitelistStatus,
  ISubAccountAuthType,
} from "@aspen/model";
import type { IWalletWhitelistAddress, TKycLevelNumber } from "@aspen/model";

import { useRouter } from "next/router";
import styles from "@aspen/theme/Wallet.module.less";
import { Row, Col, Button, Modal, Spin, Tabs, Table, Tooltip } from "antd";
import {
  GA_EVENT_NAME,
  WALLET_PATHS,
  convertUSD2USDC,
  getQueryValue,
  i18nUtil,
  reportEvent,
  hasPermissionOrNotSubAccount,
  getCountryDisplayName,
  MEMO_COINS,
} from "@aspen/libs";
import Image from "next/image";
import { deleteWhitelistAddress, getWhitelistAddress } from "@aspen/services";
import { message, ModalBase, WithKYCInfo } from "@aspen/ui";
import ModalWhitelistDetail from "@aspen/widgets/wallet/modal/ModalWhitelistDetail";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { CopyToClipboard } from "react-copy-to-clipboard";

interface IProps {
  showGuideModal: (level: TKycLevelNumber, isClose?: boolean) => boolean;
}

interface IWalletWhitelistAddressRow extends IWalletWhitelistAddress {
  ownershipVerificationAmount?: string;
  ownershipDepositAddress?: string;
  ownershipDepositMemo?: string;
  ownershipAsset?: string;
  ownershipNetwork?: string;
}

function AdgmWhitelistAddressManage(props: IProps) {
  const tab = getQueryValue("tab");
  const intl = i18nUtil.t();
  const router = useRouter();
  const tabItems = [
    {
      label: intl["wallet.withdraw.white.list.tab.whitelisted"],
      key: WALLET_WHITELIST_TAB_TYPE.Whitelist,
    },
    {
      label: intl["wallet.withdraw.white.list.tab.reviewing"],
      key: WALLET_WHITELIST_TAB_TYPE.InReview,
    },
  ];
  const [loading, setLoading] = useState<boolean>(true);
  const [currentTab, setCurrentTab] = useState<string>(tab || WALLET_WHITELIST_TAB_TYPE.Whitelist);
  const [dataSource, setDataSource] = useState<IWalletWhitelistAddressRow[]>([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [ownershipModalVisible, setOwnershipModalVisible] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<IWalletWhitelistAddressRow>();

  const columns = useMemo(
    () => [
      {
        title: intl["column.name"],
        render: (_: unknown, record: IWalletWhitelistAddressRow) => (
          <span>{record?.customerName}</span>
        ),
      },
      {
        title: intl["column.denomination"],
        dataIndex: "currency",
        render: (value: string) => convertUSD2USDC(value),
      },
      {
        title: intl["column.network"],
        dataIndex: "chainName",
      },
      {
        title: intl["wallet.withdraw.white.add.wallet.address"],
        render: (_: unknown, record: IWalletWhitelistAddressRow) => (
          <p>
            <span>{record.address}</span>
            {(record?.currency === "XRP" || MEMO_COINS.includes(record?.currency ?? "")) && (
              <Tooltip title={record?.memo}>
                <InfoCircleOutlined style={{ paddingLeft: "5px", cursor: "pointer" }} />
              </Tooltip>
            )}
          </p>
        ),
      },
      {
        title: intl["wallet.withdraw.white.add.source.name"],
        render: (_: unknown, record: IWalletWhitelistAddressRow) => (
          <span>{record?.vaspName || "--"}</span>
        ),
      },
      ...(currentTab === WALLET_WHITELIST_TAB_TYPE.Whitelist
        ? [
            {
              title: intl["wallet.withdraw.white.add.country.of.deposit"],
              render: (_: unknown, record: IWalletWhitelistAddressRow) => (
                <span>{getCountryDisplayName(record?.country)}</span>
              ),
            },
          ]
        : []),
      ...(currentTab === WALLET_WHITELIST_TAB_TYPE.InReview
        ? [
            {
              title: intl["wallet.withdraw.white.list.ownership.test.column"],
              render: (_: unknown, record: IWalletWhitelistAddressRow) => (
                <span
                  className={styles.mainColor}
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                  onClick={() => {
                    setCurrentItem(record);
                    setOwnershipModalVisible(true);
                  }}>
                  {record?.ownershipVerificationAmount ??
                    intl["wallet.withdraw.white.list.ownership.test.install"]}
                </span>
              ),
            },
          ]
        : []),
      {
        title: "",
        render: (_: unknown, record: IWalletWhitelistAddressRow) => {
          return (
            <>
              {record?.customerType === ICustomerType.INDIVIDUAL.toUpperCase() ? (
                <span
                  className={styles.individual}
                  onClick={() => {
                    setCurrentItem(record);
                    setDetailModalVisible(true);
                  }}>
                  {ICustomerType.INDIVIDUAL}
                </span>
              ) : (
                <span
                  className={styles.institution}
                  onClick={() => {
                    setCurrentItem(record);
                    setDetailModalVisible(true);
                  }}>
                  {ICustomerType.INSTITUTION}
                </span>
              )}
            </>
          );
        },
      },
      ...(currentTab === WALLET_WHITELIST_TAB_TYPE.Whitelist
        ? [
            {
              title: intl["column.action"],
              render: (_: unknown, record: IWalletWhitelistAddressRow) => {
                return hasPermissionOrNotSubAccount(ISubAccountAuthType.WHITELIST) ? (
                  <div>
                    <Button
                      onClick={() => {
                        reportEvent({
                          moduleName: GA_EVENT_NAME.whithdraw.delete,
                        });
                        setCurrentItem(record);
                        setDeleteModalVisible(true);
                      }}>
                      {intl["wallet.withdraw.white.list.delete.address.title"]}
                    </Button>
                  </div>
                ) : null;
              },
            },
          ]
        : []),
    ],
    [currentTab, intl],
  );

  useEffect(() => {
    props.showGuideModal(1, true);
  }, []);

  useEffect(() => {
    if (currentTab) {
      fetchListData();
    }
  }, [currentTab]);

  const handleChangeTabs = (value: string) => {
    setCurrentTab(value);
  };
  const fetchListData = () => {
    setLoading(true);
    const param = {
      statuses:
        currentTab === WALLET_WHITELIST_TAB_TYPE.Whitelist
          ? WhitelistStatus.APPROVED
          : WhitelistStatus.CREATED,
    };
    getWhitelistAddress(param)
      .then(res => {
        if (res?.code == "0") {
          const rows = (res?.data ?? []).map((item: IWalletWhitelistAddress) => ({
            ...item,
            ownershipVerificationAmount:
              currentTab === WALLET_WHITELIST_TAB_TYPE.InReview
                ? item.smallTransactionTest?.amount?.toString() || "--"
                : undefined,
            ownershipDepositAddress:
              currentTab === WALLET_WHITELIST_TAB_TYPE.InReview
                ? item.smallTransactionTest?.address || "--"
                : undefined,
            ownershipDepositMemo:
              currentTab === WALLET_WHITELIST_TAB_TYPE.InReview
                ? item.smallTransactionTest?.memo || "--"
                : undefined,
            ownershipAsset:
              currentTab === WALLET_WHITELIST_TAB_TYPE.InReview
                ? item.smallTransactionTest?.asset || item.currency
                : undefined,
            ownershipNetwork:
              currentTab === WALLET_WHITELIST_TAB_TYPE.InReview
                ? item.smallTransactionTest?.network || item.chainName
                : undefined,
          }));
          setDataSource(rows);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const deleteAddress = () => {
    if (!currentItem) return;
    const params = {
      addressId: currentItem.id,
    };
    reportEvent({
      moduleName: GA_EVENT_NAME.whithdraw.deleteAddress,
      detailParams: params,
    });
    deleteWhitelistAddress(params).then(res => {
      if (res?.code == "0") {
        setTimeout(() => {
          fetchListData();
        }, 1000);
        message.success(intl["wallet.withdraw.white.list.delete.address.success"]);
      }
    });
  };
  const renderEmptyView = () => {
    return (
      <div className={styles.emptyView}>
        <Image
          unoptimized
          alt=""
          width="88"
          height="88"
          src={require("@aspen/assets/images/addressWhitelist/noAddress.webp").default}
        />
        <p>{intl["wallet.withdraw.select.address.noAddress"]}</p>
        <div className={styles.addAddress}>
          <span
            onClick={() => {
              router.push(WALLET_PATHS.WALLET_WITHDRAW_ADD_ADDRESS);
            }}>
            {intl["wallet.withdraw.white.list.bottom.modal.noAddress.btn"]}
          </span>
        </div>
        <p>{intl["wallet.withdraw.white.list.empty.text3"]}</p>
      </div>
    );
  };

  return (
    <section className={`${styles.withdrawAddressManage} ` + `customer`}>
      <div className="customerTop">
        <Row className="top">
          <Col span={24}>
            <div className={styles.topTitleArea}>
              <div className={styles.leftTitleArea}>
                <span className="title">{intl["wallet.withdraw.white.list.title"]}</span>
              </div>
              <div className={styles.rightTitleArea}>
                <Button
                  type="primary"
                  className={styles.allBtn}
                  style={{
                    minWidth: 109,
                  }}
                  onClick={() => {
                    reportEvent({ moduleName: GA_EVENT_NAME.whithdraw.previewHistory });
                    router.push(WALLET_PATHS.WALLET_WITHDRAW_WHITELIST_HISTORY);
                  }}>
                  {intl["button.withdraw.white.list.history"]}
                </Button>
                <Button
                  type="primary"
                  className={styles.allBtn}
                  onClick={() => {
                    reportEvent({ moduleName: GA_EVENT_NAME.whithdraw.previewAdd });
                    router.push(WALLET_PATHS.WALLET_WITHDRAW_ADD_ADDRESS);
                  }}>
                  {intl["button.withdraw.white.list.add.address"]}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <div className="section">
        <Spin spinning={loading}>
          <Tabs
            items={tabItems}
            className={styles.tab}
            activeKey={currentTab}
            onChange={handleChangeTabs}
          />

          {dataSource?.length ? (
            <div className={styles.tableView}>
              <Table
                className={styles.table}
                bordered={false}
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                scroll={{ x: "max-content" }}
              />
            </div>
          ) : (
            renderEmptyView()
          )}
        </Spin>
      </div>
      {deleteModalVisible ? (
        <ModalBase
          open={deleteModalVisible}
          title={intl["wallet.withdraw.white.list.delete.address.title"]}
          onOk={() => {
            reportEvent({ moduleName: GA_EVENT_NAME.whithdraw.confirmDelete });
            setDeleteModalVisible(false);
            deleteAddress();
          }}
          okText={intl["button.confirm"]}
          onCancel={() => {
            setDeleteModalVisible(false);
          }}>
          <div>{intl["wallet.withdraw.white.list.delete.address.content"]}</div>
        </ModalBase>
      ) : null}
      {detailModalVisible ? (
        <ModalWhitelistDetail
          visible={detailModalVisible}
          handleCancel={() => setDetailModalVisible(false)}
          whitelistInfo={currentItem}
          showCountry={currentTab === WALLET_WHITELIST_TAB_TYPE.InReview}
        />
      ) : null}
      {ownershipModalVisible ? (
        <Modal
          destroyOnClose
          wrapClassName={styles.modal}
          open={ownershipModalVisible}
          title={intl["wallet.withdraw.white.add.ownership.test.title"]}
          centered
          footer={null}
          width={480}
          onCancel={() => {
            setOwnershipModalVisible(false);
          }}>
          <div className={styles.middleArea}>
            <p style={{ color: "var(--white-op2)", lineHeight: "22px", marginBottom: 16 }}>
              {intl["wallet.withdraw.white.add.ownership.test.instruction"]}
            </p>
            <p style={{ color: "var(--white-op8)", lineHeight: "22px", marginBottom: 16 }}>
              {intl["wallet.withdraw.white.add.ownership.test.deposit.section"]}
            </p>
            <p>
              <span className={styles.middleKey}>
                {intl["wallet.withdraw.white.history.denomination"]}
              </span>
              <span className={styles.middleValue}>
                {convertUSD2USDC(currentItem?.ownershipAsset || currentItem?.currency || "")}
              </span>
            </p>
            <p>
              <span className={styles.middleKey}>
                {intl["wallet.withdraw.white.history.network"]}
              </span>
              <span className={styles.middleValue}>
                {currentItem?.ownershipNetwork || currentItem?.chainName || "--"}
              </span>
            </p>
            <p>
              <span className={styles.middleKey}>
                {intl["wallet.withdraw.white.add.ownership.test.verification.amount"]}
              </span>
              <span className={styles.middleValue}>
                {currentItem?.ownershipVerificationAmount || "--"}
                {currentItem?.ownershipVerificationAmount ? (
                  <CopyToClipboard
                    text={currentItem.ownershipVerificationAmount}
                    onCopy={() => message.success(intl["copied"])}>
                    <CopyOutlined style={{ marginLeft: 8, cursor: "pointer" }} />
                  </CopyToClipboard>
                ) : null}
              </span>
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}>
              <span className={styles.middleKey} style={{ whiteSpace: "nowrap", marginRight: 16 }}>
                {intl["wallet.withdraw.white.add.ownership.test.deposit.address"]}
              </span>
              <span
                className={styles.middleValue}
                style={{ display: "flex", alignItems: "center", gap: 8, maxWidth: 270 }}>
                <span
                  style={{
                    textAlign: "right",
                    wordBreak: "break-all",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    color: "var(--white)",
                    fontFamily: "var(--CommonMedium)",
                    lineHeight: "22px",
                  }}>
                  {currentItem?.ownershipDepositAddress || "--"}
                </span>
                {currentItem?.ownershipDepositAddress ? (
                  <CopyToClipboard
                    text={currentItem.ownershipDepositAddress}
                    onCopy={() => message.success(intl["copied"])}>
                    <CopyOutlined style={{ cursor: "pointer", flexShrink: 0 }} />
                  </CopyToClipboard>
                ) : null}
              </span>
            </div>
            {currentItem?.ownershipDepositMemo?.trim() &&
            currentItem.ownershipDepositMemo !== "--" ? (
              <>
                <p>
                  <span className={styles.middleKey}>{intl["wallet.withdraw.white.add.tag"]}</span>
                  <span className={styles.middleValue}>
                    {currentItem.ownershipDepositMemo}
                    <CopyToClipboard
                      text={currentItem.ownershipDepositMemo}
                      onCopy={() => message.success(intl["copied"])}>
                      <CopyOutlined style={{ marginLeft: 8, cursor: "pointer" }} />
                    </CopyToClipboard>
                  </span>
                </p>
                <p>
                  <span className={styles.middleKey}>
                    {intl["wallet.withdraw.white.add.ownership.test.memo.label"]}
                  </span>
                  <span className={styles.middleValue}>
                    {currentItem.ownershipDepositMemo}
                    <CopyToClipboard
                      text={currentItem.ownershipDepositMemo}
                      onCopy={() => message.success(intl["copied"])}>
                      <CopyOutlined style={{ marginLeft: 8, cursor: "pointer" }} />
                    </CopyToClipboard>
                  </span>
                </p>
              </>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

export const PageAdgmWhitelistAddressManage = WithKYCInfo(AdgmWhitelistAddressManage);
